'use client';

import { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, Edit, Plus, Loader2, Search, X } from 'lucide-react';
import { showError, showSuccess } from '@/utils/toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Brand {
  id: number;
  name: string;
}

interface Category {
  id: number;
  code: string;
  name?: string;
}

interface Product {
  id: number;
  name: string;
  category_id: number;
  brand_id: number | null;
  brand_name: string | null;
  ean_code: string | null;
  current_price: number | null;
  current_price_currency: string | null;
  instances_count: number;
}

interface FormData {
  name: string;
  category_id: string;
  brand_id: string;
  ean_code: string;
  description: string;
}

const ProductsPage = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    category_id: '',
    brand_id: '',
    ean_code: '',
    description: '',
  });

  // Betöltés az alkalmazás induláskor
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchProducts(),
          fetchCategories(),
          fetchBrands(),
        ]);
      } catch (error) {
        console.error('Hiba az adatok betöltésekor:', error);
        showError('Hiba az adatok betöltésekor');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Termékek betöltése
  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      if (selectedCategory) params.append('category_id', selectedCategory);

      const response = await fetch(`/api/admin/products?${params}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Hiba a termékek betöltésekor');

      const data = await response.json();
      setProducts(data.products || []);
    } catch (error) {
      console.error('Hiba a termékek betöltésekor:', error);
      throw error;
    }
  };

  // Kategóriák betöltése
  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/admin/categories', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Hiba a kategóriák betöltésekor');

      const data = await response.json();
      setCategories(data.categories || []);
    } catch (error) {
      console.error('Hiba a kategóriák betöltésekor:', error);
    }
  };

  // Márkák betöltése
  const fetchBrands = async () => {
    try {
      const response = await fetch('/api/admin/brands', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Hiba a márkák betöltésekor');

      const data = await response.json();
      setBrands(data.brands || []);
    } catch (error) {
      console.error('Hiba a márkák betöltésekor:', error);
    }
  };

  // Termék szerkesztésének megnyitása
  const handleEdit = (product: Product) => {
    setIsEditing(true);
    setEditingId(product.id);
    setFormData({
      name: product.name,
      category_id: product.category_id.toString(),
      brand_id: product.brand_id?.toString() || '',
      ean_code: product.ean_code || '',
      description: '',
    });
    setIsDialogOpen(true);
  };

  // Új termék hozzáadásának megnyitása
  const handleNewProduct = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData({
      name: '',
      category_id: '',
      brand_id: '',
      ean_code: '',
      description: '',
    });
    setIsDialogOpen(true);
  };

  // Termék mentése
  const handleSave = async () => {
    if (!formData.name.trim() || !formData.category_id) {
      showError('Kérjük töltse ki a kötelező mezőket');
      return;
    }

    setIsSubmitting(true);
    try {
      const url = isEditing
        ? `/api/admin/products/${editingId}`
        : '/api/admin/products';

      const method = isEditing ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          product: {
            name: formData.name,
            category_id: parseInt(formData.category_id),
            brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
            ean_code: formData.ean_code || null,
            description: formData.description || null,
          },
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.errors?.[0] || 'Hiba a termék mentésekor');
      }

      showSuccess(isEditing ? 'Termék frissítve' : 'Termék hozzáadva');
      setIsDialogOpen(false);
      await fetchProducts();
    } catch (error) {
      console.error('Hiba a termék mentésekor:', error);
      showError(error instanceof Error ? error.message : 'Hiba a termék mentésekor');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Termék törlése
  const handleDelete = async (id: number) => {
    if (!confirm('Biztosan törlöd ezt a terméket?')) return;

    try {
      const response = await fetch(`/api/admin/products/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Hiba a termék törlésekor');

      showSuccess('Termék törölve');
      await fetchProducts();
    } catch (error) {
      console.error('Hiba a termék törlésekor:', error);
      showError('Hiba a termék törlésekor');
    }
  };

  // Szűrés keresésre
  const handleSearch = async () => {
    await fetchProducts();
  };

  // Szűrés kategóriára
  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setSearchQuery('');
  };

  // Szűrés törlése
  const clearFilters = async () => {
    setSearchQuery('');
    setSelectedCategory('');
    await fetchProducts();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-primary">Termékek</h1>
        <Button onClick={handleNewProduct} className="gap-2">
          <Plus className="w-4 h-4" />
          Új termék
        </Button>
      </div>

      {/* Szűrés */}
      <div className="flex gap-2 flex-wrap items-end">
        <div className="flex-1 min-w-64">
          <Label className="text-sm font-medium">Keresés név/EAN alapján</Label>
          <div className="flex gap-2 mt-1">
            <Input
              placeholder="Termék neve vagy EAN kódja..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button onClick={handleSearch} variant="outline" size="icon">
              <Search className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="min-w-64">
          <Label className="text-sm font-medium">Kategória</Label>
          <Select value={selectedCategory} onValueChange={handleCategoryChange}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Összes kategória" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id.toString()}>
                  {cat.code} {cat.name && `- ${cat.name}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {(searchQuery || selectedCategory) && (
          <Button onClick={clearFilters} variant="ghost" size="icon">
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Termék táblázat */}
      {products.length === 0 ? (
        <div className="text-center py-8 text-foreground/50">
          Nincs talált termék
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Termék név</TableHead>
                <TableHead>EAN kód</TableHead>
                <TableHead>Márka</TableHead>
                <TableHead className="text-right">Aktuális ár</TableHead>
                <TableHead className="text-center">Példányok száma</TableHead>
                <TableHead className="text-right">Műveletek</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id}>
                  <TableCell className="font-medium">{product.name}</TableCell>
                  <TableCell className="text-sm text-foreground/70">
                    {product.ean_code || '-'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {product.brand_name || '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {product.current_price
                      ? `${product.current_price} ${product.current_price_currency || ''}`
                      : '-'}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-primary/10 text-primary font-medium text-sm">
                      {product.instances_count}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        onClick={() => handleEdit(product)}
                        variant="ghost"
                        size="icon"
                        title="Szerkesztés"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(product.id)}
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        title="Törlés"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Dialog az új/szerkesztett termékhez */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Termék szerkesztése' : 'Új termék'}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Módosítsd a termék adatait'
                : 'Adj meg információt az új termékről'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Termék neve *</Label>
              <Input
                id="name"
                placeholder="Termék neve"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>

            <div>
              <Label htmlFor="category">Kategória *</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) =>
                  setFormData({ ...formData, category_id: value })
                }
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="Válassz kategóriát" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      {cat.code} {cat.name && `- ${cat.name}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="brand">Márka</Label>
              <Select
                value={formData.brand_id}
                onValueChange={(value) =>
                  setFormData({ ...formData, brand_id: value })
                }
              >
                <SelectTrigger id="brand">
                  <SelectValue placeholder="Válassz márkát (opcionális)" />
                </SelectTrigger>
                <SelectContent>
                  {brands.map((brand) => (
                    <SelectItem key={brand.id} value={brand.id.toString()}>
                      {brand.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="ean">EAN kód</Label>
              <Input
                id="ean"
                placeholder="EAN kód (opcionális)"
                value={formData.ean_code}
                onChange={(e) =>
                  setFormData({ ...formData, ean_code: e.target.value })
                }
              />
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                disabled={isSubmitting}
              >
                Mégsem
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSubmitting}
                className="gap-2"
              >
                {isSubmitting && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                {isEditing ? 'Frissítés' : 'Hozzáadás'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;